import { useEffect, useState } from 'react';


import { useAreaData } from './useAreaData.js';
import { useDotData } from './useDotData.js';
import { useLineData } from './useLineData.js';
import { useXYChartData } from './useXYChartData.js';
import { useXYChartScales } from './useXYChartScales.js';
import { useId } from '../../../core/hooks/useId.js';
import type { SeriesToQuadtree } from '../context/XYChartOverlayPerformance.context.js';
import type { AnyLinearSeries } from '../types/xy-chart-internals.js';
import {
  cancelDebouncedQuadtrees,
  getDebouncedQuadtrees,
} from '../utils/query-closest-quadtree.js';

export function useBuildQuadtree() {
  const { filteredSeriesWithDatapoints: dotSeriesWithDatapoints } =
    useDotData();
  const { filteredSeriesWithDatapoints: lineSeriesWithDatapoints } =
    useLineData();
  const { filteredSeriesWithDatapoints: areaSeriesWithDatapoints } =
    useAreaData();
  const axisIdToScales = useXYChartScales();
  const { datapointMetadataMap, axisIdToScaleDomain, seriesToMetadata } =
    useXYChartData();
  const id = useId();
  const [seriesIdToQuadtree, setSeriesIdToQuadtree] = useState<
    SeriesToQuadtree | undefined
  >(undefined);

  useEffect(() => {
    const linearSeries: AnyLinearSeries[] = [
      ...dotSeriesWithDatapoints,
      ...lineSeriesWithDatapoints,
      ...areaSeriesWithDatapoints,
    ];

    const debounced = getDebouncedQuadtrees(id);

    debounced(
      linearSeries,
      axisIdToScales,
      datapointMetadataMap,
      seriesToMetadata,
      axisIdToScaleDomain,
      setSeriesIdToQuadtree,
    );

    return () => {
      cancelDebouncedQuadtrees(id);
      setSeriesIdToQuadtree(undefined);
    };
  }, [
    dotSeriesWithDatapoints,
    lineSeriesWithDatapoints,
    areaSeriesWithDatapoints,
    datapointMetadataMap,
    axisIdToScales,
    axisIdToScaleDomain,
    seriesToMetadata,
    id,
  ]);

  return seriesIdToQuadtree;
}
